/**
 * Put `text` on the clipboard, and say whether it got there.
 *
 * The clipboard API is refused outside a secure context, which a plain
 * http:// page is not everywhere, and is missing altogether from older
 * Safari, so a hidden textarea and execCommand is kept as the fallback.
 * Either way it has to be called from inside the click that asked for it:
 * a browser lets a page write to the clipboard only while somebody is
 * pressing something. The map viewer's copies (mapview.ts) and the log's
 * Copy message (ui.ts) both come through here.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // No clipboard API, or one that said no: the old way.
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  // Off to one side and read-only, so nothing scrolls to it and no
  // phone's keyboard comes up for it.
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  const was = document.activeElement as HTMLElement | null;
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length); // iOS selects nothing on select()
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  was?.focus?.();
  return ok;
}
